/**
 * Framework Agreement listing SEGMENTS (frontend config).
 *
 * The backend owns the segment definitions and returns a facet count per counted segment
 * alongside the list. This file holds the presentation metadata: labels, tab order, route
 * slugs (kebab-case), which segments carry a count, and a role-aware description shown under
 * the active tab. Keys mirror the backend `FrameworkAgreementSegment` enum exactly.
 */

import { ROLE_CODES } from './userRoles';

export const FW_SEGMENT = Object.freeze({
  ALL: 'ALL',
  SELF_DRAFTS: 'SELF_DRAFTS',
  TEAM_DRAFTS: 'TEAM_DRAFTS',
  AWAITING_SELF_APPROVAL: 'AWAITING_SELF_APPROVAL',
  AWAITING_OTHERS_APPROVAL: 'AWAITING_OTHERS_APPROVAL',
  ONLY_ACTIVE: 'ONLY_ACTIVE',
});

/** The default segment (landing tab / bare URL). */
export const DEFAULT_SEGMENT = FW_SEGMENT.ALL;

/**
 * Where the module is entered from Home / the side nav / the toolbar Reset: the user's own
 * drafts. (A bare `/framework-agreements` URL still normalises to DEFAULT_SEGMENT.)
 */
export const LANDING_SEGMENT = FW_SEGMENT.SELF_DRAFTS;
export const LANDING_PATH = '/framework-agreements/self-drafts';

/** Segments that carry a facet count (ALL and ONLY_ACTIVE are count-less by design). */
const COUNTED = new Set([
  FW_SEGMENT.SELF_DRAFTS,
  FW_SEGMENT.TEAM_DRAFTS,
  FW_SEGMENT.AWAITING_SELF_APPROVAL,
  FW_SEGMENT.AWAITING_OTHERS_APPROVAL,
]);

/** Kebab-case route slug for each segment (URL uses these, like the other list pages). */
const SLUG_BY_SEGMENT = Object.freeze({
  [FW_SEGMENT.ALL]: 'all',
  [FW_SEGMENT.SELF_DRAFTS]: 'self-drafts',
  [FW_SEGMENT.TEAM_DRAFTS]: 'team-drafts',
  [FW_SEGMENT.AWAITING_SELF_APPROVAL]: 'awaiting-self-approval',
  [FW_SEGMENT.AWAITING_OTHERS_APPROVAL]: 'awaiting-others-approval',
  [FW_SEGMENT.ONLY_ACTIVE]: 'only-active',
});
const SEGMENT_BY_SLUG = Object.freeze(
  Object.fromEntries(
    Object.entries(SLUG_BY_SEGMENT).map(([seg, slug]) => [slug, seg])
  )
);

export const segmentToSlug = (segment) =>
  SLUG_BY_SEGMENT[segment] ?? SLUG_BY_SEGMENT[DEFAULT_SEGMENT];
export const slugToSegment = (slug) => SEGMENT_BY_SLUG[slug] ?? null;

const LABELS = Object.freeze({
  [FW_SEGMENT.ALL]: 'All',
  [FW_SEGMENT.AWAITING_SELF_APPROVAL]: 'Needs Your Action',
  [FW_SEGMENT.AWAITING_OTHERS_APPROVAL]: 'Awaiting Others',
  [FW_SEGMENT.SELF_DRAFTS]: 'Your Drafts',
  [FW_SEGMENT.TEAM_DRAFTS]: "Team's Drafts",
  [FW_SEGMENT.ONLY_ACTIVE]: 'Active',
});

/** Tab order (ALL first, then the actionable ones, then browse). */
const ORDER = Object.freeze([
  FW_SEGMENT.ALL,
  FW_SEGMENT.AWAITING_SELF_APPROVAL,
  FW_SEGMENT.AWAITING_OTHERS_APPROVAL,
  FW_SEGMENT.SELF_DRAFTS,
  FW_SEGMENT.TEAM_DRAFTS,
  FW_SEGMENT.ONLY_ACTIVE,
]);

/**
 * Description shown under the active tab. Each segment has a `default` line and, where the
 * meaning differs by seniority, role-specific overrides (picked by the caller's primary
 * role). Kept plain so the tab component can render it directly.
 */
const DESCRIPTIONS = Object.freeze({
  [FW_SEGMENT.ALL]: {
    default:
      'You are viewing <b>every framework agreement you have access to</b>, across all of your offices and departments and regardless of its current stage, based on your <b>selected search and filter criteria</b>. This is your <b>complete working list</b> for locating any agreement, whether it is a draft, in approval, or already active. If you cannot find a specific agreement, try <b>adjusting your search or filter settings</b>.',
  },
  [FW_SEGMENT.SELF_DRAFTS]: {
    default:
      'You are viewing <b>draft framework agreements you created</b> that have <b>not yet been sent forward</b> for review. Complete the remaining details and <b>submit them for review</b> to move them into the approval workflow. Keeping this list short ensures your own work does not <b>stall before it begins</b> — anything sitting here is waiting on you to progress it.',
  },
  [FW_SEGMENT.TEAM_DRAFTS]: {
    default:
      'You are viewing <b>draft framework agreements started by your team</b> that have <b>not yet been sent for review</b>. Use this to <b>follow up with the people responsible</b> so that nothing sits idle before it enters approval. If a draft looks stuck, a timely nudge is often all it takes to <b>keep it moving</b>.',
    [ROLE_CODES.HEAD_OF_DEPARTMENT]:
      'You are viewing <b>draft framework agreements within your department</b> that are <b>still unsent</b>. These belong to people you lead, so <b>encourage the owners to complete and submit them for review</b> before they lose momentum. Regularly clearing idle drafts keeps your department’s pipeline <b>healthy and predictable</b>.',
    [ROLE_CODES.REGIONAL_MANAGER]:
      'You are viewing <b>draft framework agreements across your office</b> that are <b>still unsent</b>. Prompt the relevant teams to <b>complete and submit them for review</b> so that work does not lose momentum early on. Reviewing this regularly helps you <b>spot bottlenecks</b> before they hold up the wider pipeline.',
    [ROLE_CODES.C_LEVEL]:
      'You are viewing <b>every draft framework agreement across the company</b> that has <b>not yet been sent for review</b>. This is a company-wide view of work that may be <b>stalling before it even starts</b>. Use it to identify offices where drafts are <b>piling up</b> and to prompt the responsible managers to follow up with their teams.',
  },
  [FW_SEGMENT.AWAITING_SELF_APPROVAL]: {
    default:
      'You are viewing <b>framework agreements that need your action right now</b> — either your <b>approval</b> as the responsible reviewer, or your <b>revisions in response to a requested change</b>. These are your <b>most time-sensitive items</b>, and clearing them promptly keeps the approval pipeline moving for everyone downstream. Anything remaining here is <b>waiting directly on you</b>.',
  },
  [FW_SEGMENT.AWAITING_OTHERS_APPROVAL]: {
    default:
      'You are viewing <b>framework agreements you moved forward</b> that are now <b>with someone else</b> — either awaiting their <b>approval</b> or the <b>changes they were asked to make</b>. Use this to <b>follow up with whoever is holding it</b> and unblock anything that has been waiting longer than it should. It is the best way to keep visibility of work you have <b>already handed off</b> but still care about closing.',
    [ROLE_CODES.C_LEVEL]:
      'You are viewing <b>every framework agreement in the company that is still in flight with someone</b> — awaiting an <b>approval</b> or the <b>changes a reviewer asked for</b>. Use this to see where work is <b>stuck across offices</b> and to <b>push the relevant managers</b> to act on their pending items. It gives you an organisation-wide picture of <b>where the approval pipeline is slowing down</b>.',
  },
  [FW_SEGMENT.ONLY_ACTIVE]: {
    default:
      'You are viewing <b>framework agreements that are currently active</b> and available to use as the basis for new work. These have completed the approval process and are <b>in effect today</b>. Use this list whenever you need to <b>reference or build on an existing active agreement</b>.',
  },
});

/** The description for a segment, tailored to the caller's primary role (or the default). */
export const segmentDescription = (segment, role) => {
  const entry = DESCRIPTIONS[segment];
  if (!entry) return '';
  return (role && entry[role]) || entry.default || '';
};

/**
 * Build the ordered tab items for the segmented control.
 *
 * @param {Object} counts - The `segmentCounts` map from the list response.
 * @param {string|null} role - The caller's primary role (for descriptions).
 * @returns {Array<{ id, slug, label, description, count? }>}
 */
export const getSegmentTabs = (counts = {}, role = null) =>
  ORDER.map((segment) => {
    const item = {
      id: segment,
      slug: segmentToSlug(segment),
      label: LABELS[segment],
      description: segmentDescription(segment, role),
    };
    if (COUNTED.has(segment)) {
      item.count = Number(counts?.[segment]) || 0;
    }
    return item;
  });
